import { NodeType, DelegationType } from '../types.js';
import notificationService from './notificationService.js';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function resolveTemplate(template, context) {
    return template.replace(/\{\{([^}]+)\}\}/g, (_, path) => {
        const parts = path.trim().split('.');
        let current = context;
        for (const part of parts) {
            if (current && typeof current === 'object' && part in current) {
                current = current[part];
            } else {
                return `{{${path}}}`; // Return original if path not found
            }
        }
        return typeof current === 'object' ? JSON.stringify(current) : current;
    });
}

// Function to handle delegation during workflow execution
async function handleDelegation(node, context, log) {
    const delegation = node.data.delegation;
    
    if (!delegation || delegation.delegationType === DelegationType.NONE) {
        return null; // No delegation, continue normal execution
    }
    
    if (delegation.delegationType === DelegationType.HUMAN) {
        const contactName = delegation.delegateToHuman.contactName || 'Unknown Contact';
        log(node.id, `Delegating to human: ${contactName}. Waiting for response...`, 'info');
        
        // Find the contact object
        const contact = {
            id: delegation.delegateToHuman.contactId,
            name: contactName,
            email: delegation.delegateToHuman.contactEmail || '',
            role: delegation.delegateToHuman.contactRole || ''
        };
        
        // Send notification to the contact
        try {
            await notificationService.sendDelegationNotification(
                node,
                contact,
                delegation.delegateToHuman.instructions
            );
            log(node.id, `Notification sent to ${contactName}.`, 'info');
        } catch (error) {
            log(node.id, `Failed to send notification to ${contactName}: ${error.message}`, 'error');
        }
        
        // In a real implementation, this would wait for the contact's response
        // For now, we'll simulate a delay based on expected response time
        const responseTime = delegation.delegateToHuman.expectedResponseTime || 5;
        log(node.id, `Waiting for ${responseTime} hours for response...`, 'info');
        await delay(Math.min(responseTime * 1000, 10000)); // Cap at 10 seconds for demo
        
        log(node.id, `Received response from ${contactName}. Continuing execution.`, 'info');
        
        // Send completion notification
        try {
            await notificationService.sendCompletionNotification(
                node,
                contact,
                `Task "${node.label}" has been completed by ${contactName}.`
            );
        } catch (error) {
            log(node.id, `Failed to send completion notification: ${error.message}`, 'error');
        }
        
        return { status: 'completed', delegatedTo: contactName };
    }
    
    if (delegation.delegationType === DelegationType.AGENT) {
        const agentName = delegation.delegateToAgent.agentName || 'Unknown Agent';
        log(node.id, `Delegating to agent: ${agentName}. Processing...`, 'info');
        
        // In a real implementation, this would call the agent service
        // For now, we'll simulate agent processing with a delay
        await delay(3000); // Simulate agent processing time
        
        log(node.id, `Processing completed by ${agentName}.`, 'info');
        return { status: 'completed', delegatedTo: agentName };
    }
    
    return null; // Unknown delegation type
}

export async function executeWorkflow({ nodes, edges, credentials, onLog, onSetActiveNode }) {
    const context = {};
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    const log = (nodeId, message, type = 'info') => {
        onLog({ timestamp: new Date().toISOString(), nodeId, message, type });
    };

    try {
        log(null, 'Workflow execution started.');
        
        // More robust way to find the starting node
        let currentNodeId = nodes.find(n => n.type === NodeType.TRIGGER)?.id || 
                           nodes.find(n => n.type === 'TRIGGER')?.id || 
                           (nodes.length > 0 ? nodes[0].id : null);

        if (!currentNodeId) {
            throw new Error('No starting node found. Please add a TRIGGER node or any node to start the workflow.');
        }

        while (currentNodeId) {
            onSetActiveNode(currentNodeId);
            const node = nodeMap.get(currentNodeId);
            if (!node) {
                throw new Error(`Node with ID ${currentNodeId} not found.`);
            }

            log(node.id, `Executing node: ${node.label} (${node.type})`);
            
            // Check for delegation before executing the node
            const delegationResult = await handleDelegation(node, context, log);
            
            let output = null;
            let nextNodeId = null;
            let nextSourceHandle = undefined;

            // Resolve templates for all data fields
            const resolvedData = {};
            for (const key in node.data) {
                if (typeof node.data[key] === 'string') {
                    resolvedData[key] = resolveTemplate(node.data[key], context);
                } else {
                    resolvedData[key] = node.data[key];
                }
            }

            // If delegation was handled, skip normal node execution
            if (delegationResult) {
                output = { status: 'delegated', result: delegationResult };
            } else {
                switch (node.type) {
                    case NodeType.TRIGGER:
                        output = { message: 'Workflow triggered', timestamp: new Date().toISOString(), user: { name: 'John Doe', email: 'john.doe@example.com', isSubscribed: true } };
                        log(node.id, 'Trigger activated with mock data.');
                        break;
                    
                    case NodeType.WAIT:
                        const duration = parseFloat(resolvedData.duration || '1');
                        const unit = resolvedData.unit || 'seconds';
                        let ms = duration * 1000;
                        if (unit === 'minutes') ms *= 60;
                        if (unit === 'hours') ms *= 60 * 60;
                        if (unit === 'days') ms *= 60 * 60 * 24;
                        log(node.id, `Waiting for ${duration} ${unit}...`);
                        await delay(ms);
                        output = { waited: `${duration} ${unit}` };
                        break;

                    case NodeType.SEND_EMAIL:
                        log(node.id, `Simulating sending email to: ${resolvedData.to} with subject: ${resolvedData.subject}`);
                        output = { status: 'sent', recipient: resolvedData.to };
                        break;

                    case NodeType.LOGGER:
                        log(node.id, resolvedData.message, 'log');
                        output = { loggedMessage: resolvedData.message };
                        break;

                    case NodeType.CODE:
                        try {
                            const code = `
                                return (async (context) => {
                                    ${node.data.code}
                                })(context);
                            `;
                            const func = new Function('context', code);
                            output = await func(context);
                            log(node.id, `Code executed successfully. Output: ${JSON.stringify(output)}`);
                        } catch (e) {
                            throw new Error(`Error in CODE node: ${e.message}`);
                        }
                        break;

                    case NodeType.IF_CONDITION:
                        // This is a mock evaluation. A real implementation would need a secure sandbox.
                        const condition = resolvedData.condition.toLowerCase();
                        let result = false;
                        if (condition.includes('==') || condition.includes('is')) {
                            result = condition.includes('true');
                        }
                        log(node.id, `Condition "${resolvedData.condition}" evaluated to: ${result}`);
                        nextSourceHandle = result ? 'true' : 'false';
                        output = { result };
                        break;

                    case NodeType.LOOP_START:
                        log(node.id, "Looping is not yet supported in execution.", 'info');
                        // For now, we just exit the loop path
                        nextSourceHandle = 'afterLoop';
                        output = { status: 'skipped' };
                        break;
                    
                    // Mock execution for other nodes
                    case NodeType.SEND_MESSAGE:
                        const person = node.assignedPerson || resolvedData.assignedPerson;
                        if (!person || !person.name) {
                            throw new Error('No person assigned to receive the message');
                        }
                        log(node.id, `Sending ${resolvedData.channel} message to ${person.name}`);
                        output = {
                            status: 'sent',
                            recipient: person.name,
                            channel: resolvedData.channel,
                            messageContent: resolvedData.message
                        };
                        break;

                    case NodeType.GENERATE_DOCUMENT:
                    case NodeType.FILE_TAXES:
                    case NodeType.UPDATE_CRM:
                    case NodeType.SOCIAL_POST:
                    case NodeType.QUERY_DATABASE:
                    case NodeType.CONNECT_API:
                    case NodeType.BROWSING_AGENT:
                    case NodeType.REGISTER_BUSINESS:
                    case NodeType.EXPAND_BUSINESS:
                    case NodeType.DASHBOARD:
                    case NodeType.CREATE_WEBSITE:
                    case NodeType.MANAGE_INVOICES:
                    case NodeType.MARKET_RESEARCH:
                    case NodeType.COMPLIANCE_CHECK:
                    case NodeType.HIRE_EMPLOYEE:
                        log(node.id, `Simulating action for ${node.type}.`);
                        output = { status: 'simulated_success' };
                        break;

                    default:
                         output = { status: 'no_op' };
                }
            }
            
            context[node.id] = { output };
            
            // Find next node
            if (node.type === NodeType.LOOP_END) {
                 // Should find corresponding LOOP_START and evaluate condition. Not implemented.
                 nextNodeId = null;
            } else {
                const nextEdge = edges.find(e => e.source === currentNodeId && e.sourceHandle === nextSourceHandle);
                nextNodeId = nextEdge?.target || null;
            }
            
            // If there's no specific next node from a handle, try the default output
            if (!nextNodeId && node.type !== NodeType.IF_CONDITION && node.type !== NodeType.LOOP_START) {
                const defaultEdge = edges.find(e => e.source === currentNodeId && !e.sourceHandle);
                nextNodeId = defaultEdge?.target || null;
            }
            
            currentNodeId = nextNodeId;

            // Small delay for visualization
            if (currentNodeId) await delay(300);
        }

        log(null, 'Workflow execution finished successfully.', 'success');
        return 'SUCCESS';
    } catch (error) {
        const errorMessage = error.message;
        log(null, `Workflow execution failed: ${errorMessage}`, 'error');
        return 'FAILED';
    } finally {
        onSetActiveNode(null);
    }
}